import Joyride from 'react-joyride';
import React, { useState, useEffect } from 'react';

export default function JoyrideTour({
    steps,
    run: initialRun = false,
    continuous = true,
    spotlightClicks = true,
    showSkipButton = true,
    showProgress = true,
    onStepBefore = () => {},
    onFinish = () => {},
    stepIndex: externalStepIndex = 0,
    isModal = false,
    hideCloseButton = false,
    scrollToFirstStep = false,
    scrollToSteps = false,
    disableScrolling = true,
    callback = () => {},
}) {
    const [run, setRun] = useState(initialRun);
    const [stepsReady, setStepsReady] = useState(false);
    const [stepIndex, setStepIndex] = useState(externalStepIndex);

    const handleCallback = (data) => {
        const { type, index, status, action } = data;
        if (type === 'step:after') {
            if (action === 'next') {
                setStepIndex(index + 1);
            } else if (action === 'prev') {
                setStepIndex(index - 1);
            }
        }

        if (status === 'finished') {
            setRun(false);
            onFinish();
            return;
        }

        if (status === 'skipped') {
            setRun(false);
        }

        callback(data);

        if (action === 'close') {
            onStepBefore(index);
            return;
        }

        if (type === 'step:before') {
            onStepBefore(index);
        }
    };

    useEffect(() => {
        setStepIndex(externalStepIndex);
    }, [externalStepIndex]);

    useEffect(() => {
        if (steps && steps.length > 0) {
            if (isModal) {
                setStepsReady(true);
                return;
            }

            const allTargetsExist = steps.every((step) => {
                if (!step.target) return false;

                const targetElement =
                    typeof step.target === 'string'
                        ? document.querySelector(step.target)
                        : step.target;

                return !!targetElement;
            });

            setStepsReady(allTargetsExist);

            if (!allTargetsExist) {
                console.warn('JoyrideTour: Some target elements are not found in the DOM');
            }
        }
    }, [steps]);

    useEffect(() => {
        if (initialRun && stepsReady) {
            setRun(true);
        } else if (!initialRun) {
            setRun(false);
        }
    }, [initialRun, stepsReady]);

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex ?? 0}
            callback={handleCallback}
            continuous={continuous}
            showSkipButton={showSkipButton}
            showProgress={true}
            spotlightClicks={spotlightClicks}
            hideCloseButton={hideCloseButton}
            scrollToSteps={scrollToSteps}
            disableScrolling={disableScrolling}
            disableCloseOnEsc={false}
            disableOverlayClose={false}
            scrollOffset={130}
            scrollToFirstStep={scrollToFirstStep}
            disableOverlay={false}
            locale={{
                back: 'Back',
                close: 'Close',
                last: 'Finish',
                next: 'Next',
                open: 'Open the dialog',
                skip: 'Skip',
            }}
            styles={{
                beacon: {
                    inner: { backgroundColor: '#0061DB' },
                    outer: {
                        backgroundColor: 'rgba(0,97,219,0.2)',
                        borderColor: '#0061DB',
                    },
                },
                options: {
                    primaryColor: '#0061DB',
                    textColor: '#333',
                    zIndex: isModal ? 10010 : 10000,
                    arrowColor: '#fff',
                    overlayColor: 'rgba(0, 0, 0, 0.4)',
                },
                overlay: {
                    zIndex: isModal ? 10009 : 9999,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                },
                tooltipContainer: {
                    textAlign: 'left',
                },
                tooltip: {
                    zIndex: isModal ? 10011 : 10001,
                    fontSize: '16px',
                    padding: '0',
                    maxWidth: '400px',
                    maxHeight: '80vh',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: 'none',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
                tooltipContent: {
                    padding: '24px',
                    lineHeight: '1.5',
                    color: '#333333',
                    fontSize: '16px',
                    overflowY: 'auto', 
                    flex: '1',
                    minHeight: 0,
                    maxHeight: 'calc(80vh - 120px)',
                },
                tooltipBody: {
                    overflowY: 'auto',
                    maxHeight: 'calc(80vh - 120px)',
                    padding: '0',
                },
                tooltipTitle: {
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#1a1a1a',
                },
                tooltipFooter: {
                    padding: '16px 24px 24px 24px',
                    margin: '0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: '1px solid #f0f0f0',
                    flexShrink: 0, 
                },
                buttonNext: {
                    backgroundColor: '#0061DB',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    color: '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: '#0052b8',
                        transform: 'translateY(-1px)',
                    },
                },
                buttonBack: {
                    backgroundColor: 'transparent',
                    border: '1px solid #d1d5db',
                    color: '#6b7280',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    marginRight: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: '#f9fafb',
                        borderColor: '#9ca3af',
                    },
                },
                buttonSkip: {
                    color: '#6b7280',
                    fontSize: '14px',
                    fontWeight: '500',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: '#f3f4f6',
                        color: '#4b5563',
                    },
                },
                buttonClose: {
                    color: '#9ca3af',
                    fontSize: '18px',
                    fontWeight: '500',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    '&:hover': {
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                    },
                },
            }}
            floaterProps={{
                disableAnimation: false,
                hideArrow: false,
                styles: {
                    floater: {
                        transition: 'opacity 0.3s ease, transform 0.3s ease',
                        filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))',
                    },
                    arrow: {
                        length: 15,
                        spread: 20,
                    },
                },
                options: {
                    preventOverflow: {
                        boundariesElement: 'viewport',
                        padding: 20,
                    },
                },
            }}
        />
    );
}